/**
 * OrderQuickSheet — the fast, tap-to-open detail bottom sheet (tier 1 of the
 * two-tier mobile detail). Shows customer + contact, payment rows, the item
 * lines, and a primary action; "View full order" opens the full OrderDetailModal
 * (tier 2) which keeps all detail functionality. Runs on list data (no extra
 * fetch); the transfer receipt + timeline live in the full order.
 */

import { useLayoutEffect, useRef } from "react";
import type { OrderStatus, SellerOrderListItem } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { getMoneyState, formatEgp } from "@/modules/orders/lib/order-money";
import { relativeAgo } from "@/modules/orders/lib/order-status-display";
import { resolveItemTitle } from "@/modules/orders/lib/resolve-item-title";
import { getOrderItemImageUrl } from "@/lib/order-image";
import { ImageThumb } from "@/shared/components/PlaceholderImage";
import { getStatusMeta } from "@/modules/orders/lib/order-status-meta";
import {
  ActionGlyph,
  actVisual,
  fulVisual,
  StatusRing,
} from "@/modules/orders/components/order-row/order-visuals";
import { STATUS_GROUP_LABEL, statusGroup } from "./orders-mobile-model";
import { PayMethodGlyph } from "./MobileOrderFullBody";

type Props = {
  order: SellerOrderListItem | null;
  isDark: boolean;
  locale: string;
  onClose: () => void;
  onOpenFull: (orderId: string) => void;
  onQuickAction: (order: SellerOrderListItem, targetStatus: OrderStatus) => void;
  onOpenQuickActionSheet: (order: SellerOrderListItem) => void;
};

const MAX_LINES = 3;

export function OrderQuickSheet({
  order,
  isDark,
  locale,
  onClose,
  onOpenFull,
  onQuickAction,
  onOpenQuickActionSheet,
}: Props) {
  // Mount only while open; a fresh mount per order replays the slide-up.
  if (!order) return null;
  return (
    <Sheet
      key={order.id}
      order={order}
      isDark={isDark}
      locale={locale}
      onClose={onClose}
      onOpenFull={onOpenFull}
      onQuickAction={onQuickAction}
      onOpenQuickActionSheet={onOpenQuickActionSheet}
    />
  );
}

/**
 * Self-driven bottom sheet: the slide-up (+ backdrop fade) is animated with the
 * Web Animations API from useLayoutEffect — it runs before the first paint, so
 * the panel is already off-screen when painted (no flash / instant pop). Closing
 * animates back down, then calls onClose.
 */
function Sheet({
  order,
  isDark,
  locale,
  onClose,
  onOpenFull,
  onQuickAction,
  onOpenQuickActionSheet,
}: Props & { order: SellerOrderListItem }) {
  const backdropRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    sheetRef.current?.animate(
      [{ transform: "translateY(100%)" }, { transform: "translateY(0)" }],
      { duration: 300, easing: "cubic-bezier(0.22, 1, 0.36, 1)", fill: "both" },
    );
    backdropRef.current?.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: 250,
      fill: "both",
    });
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const close = () => {
    const sheet = sheetRef.current;
    if (!sheet || typeof sheet.animate !== "function") {
      onClose();
      return;
    }
    backdropRef.current?.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: 220,
      fill: "forwards",
    });
    const anim = sheet.animate(
      [{ transform: "translateY(0)" }, { transform: "translateY(100%)" }],
      { duration: 220, easing: "cubic-bezier(0.4, 0, 1, 1)", fill: "forwards" },
    );
    anim.onfinish = () => onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] lg:hidden">
      <div
        ref={backdropRef}
        className="absolute inset-0 bg-black/40"
        aria-hidden="true"
        onClick={close}
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        className="absolute inset-x-0 bottom-0 flex max-h-[88vh] flex-col rounded-t-[24px] border-t border-[color:var(--card-br)] bg-[color:var(--card)] shadow-[0_-24px_60px_rgba(0,0,0,0.35)]"
      >
        <div className="flex shrink-0 justify-center pb-[18px] pt-[11px]" aria-hidden="true">
          <div className="h-1 w-10 rounded-full bg-[color:var(--track)]" />
        </div>
        <Body
          order={order}
          isDark={isDark}
          locale={locale}
          onClose={close}
          onOpenFull={onOpenFull}
          onQuickAction={onQuickAction}
          onOpenQuickActionSheet={onOpenQuickActionSheet}
        />
      </div>
    </div>
  );
}

function Body({
  order,
  isDark,
  locale,
  onClose,
  onOpenFull,
  onQuickAction,
  onOpenQuickActionSheet,
}: Props & { order: SellerOrderListItem }) {
  const t = useTranslations();
  const ring = fulVisual(order.status, isDark);
  const money = getMoneyState(
    order.status,
    order.paymentMethod,
    order.paidAmount,
    order.requiredAmount,
    order.totalAmount,
  );
  const when = relativeAgo(order.createdAt, locale);
  const digits = order.customer.phoneE164.replace(/[^0-9]/g, "");

  const kind =
    order.paymentMethod === "COD"
      ? "cod"
      : order.paidAmount > 0 && order.paidAmount < order.requiredAmount
        ? "deposit"
        : "paid";
  const payRows =
    kind === "deposit"
      ? [
          { label: t(keys.orders.depositPaidLabel), amount: formatEgp(order.paidAmount), pos: true },
          {
            label: t(keys.orders.balanceDueLabel),
            amount: formatEgp(order.requiredAmount - order.paidAmount),
            pos: false,
          },
        ]
      : kind === "paid"
        ? [{ label: t(keys.orders.paidInFull), amount: formatEgp(order.paidAmount), pos: true }]
        : [
            {
              label: t(keys.orders.collectOnDelivery),
              amount: formatEgp(order.requiredAmount || order.totalAmount),
              pos: false,
            },
          ];

  const lines = order.items.slice(0, MAX_LINES);
  const extra = order.items.length - lines.length;

  const meta = getStatusMeta(order.status);
  const av = actVisual(order.status);
  const runAction = () => {
    const tr = meta.transition;
    if (!tr) {
      onOpenFull(order.id);
      return;
    }
    if (tr.needsEta || tr.requiresDeclineReason) {
      onClose();
      onOpenQuickActionSheet(order);
    } else {
      onQuickAction(order, tr.targetStatus);
      onClose();
    }
  };
  const stageLabel = t(STATUS_GROUP_LABEL[statusGroup(order.status)]);
  const methodLabel =
    order.paymentMethod === "COD"
      ? t(keys.orders.paymentMethodCod)
      : t(keys.orders.paymentMethodExternalOnline);
  const payBadge =
    kind === "cod"
      ? { label: t(keys.orders.paymentMethodCod), color: "var(--warning-500)" }
      : kind === "paid"
        ? { label: t(keys.orders.payPaidOnline), color: "var(--success-500)" }
        : { label: t(keys.orders.payDeposit), color: "#3B82F6" };
  const insetBg = "color-mix(in srgb, var(--text-primary) 6%, transparent)";

  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-y-auto px-[22px] pt-0"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)" }}
    >
      {/* header: ring + id + close */}
      <div className="mb-[5px] flex items-center gap-[11px]">
        <StatusRing color={ring.color} kind={ring.ring} frac={ring.frac} isDark={isDark} />
        <span className="text-[19px] font-extrabold tracking-[-0.01em] text-[color:var(--text-primary)]">
          #{order.orderCode}
        </span>
        <span className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          aria-label={t(keys.common.close)}
          className="flex h-[38px] w-[38px] items-center justify-center rounded-full border border-[color:var(--card-br)] bg-[color:var(--soft)] text-[color:var(--text-secondary)]"
        >
          <XIcon />
        </button>
      </div>

      {/* stage · when + payment badge */}
      <div className="mb-[15px] flex items-center gap-2.5">
        <span className="text-[13px] font-medium text-[color:var(--text-muted)]">
          {stageLabel}
          {when ? ` · ${when}` : ""}
        </span>
        <span
          className="rounded-[7px] px-[9px] py-[3px] text-[11px] font-bold"
          style={{
            background: `color-mix(in srgb, ${payBadge.color} 16%, transparent)`,
            color: payBadge.color,
          }}
        >
          {payBadge.label}
        </span>
      </div>

      {/* customer */}
      <div className="mb-[11px] flex items-center gap-2.5 rounded-[15px] bg-[color:var(--soft)] px-[13px] py-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14.5px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
            {order.customer.name || "—"}
          </div>
          <div className="mt-0.5 truncate text-[12.5px] text-[color:var(--text-muted)]">
            {order.customer.phoneE164}
            {order.address?.area ? ` · ${order.address.area}` : ""}
          </div>
        </div>
        <a
          href={`tel:${order.customer.phoneE164}`}
          aria-label={t(keys.orders.callCustomer)}
          className="flex h-[38px] w-[38px] items-center justify-center rounded-full border border-[color:var(--card-br)] bg-[color:var(--card)] text-[color:var(--text-secondary)]"
        >
          <PhoneIcon />
        </a>
        <a
          href={`https://wa.me/${digits}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WhatsApp"
          className="flex h-[38px] w-[38px] items-center justify-center rounded-full border border-[color:rgba(74,222,128,0.32)] bg-[color:rgba(74,222,128,0.14)] text-[#22A559]"
        >
          <WhatsAppIcon />
        </a>
      </div>

      {/* payment */}
      <div className="mb-[11px] rounded-[15px] bg-[color:var(--soft)] px-[14px] py-[13px]">
        <div className="mb-[11px] flex items-center justify-between gap-2.5">
          <span className="text-[11px] font-bold uppercase tracking-[0.05em] text-[color:var(--text-muted)]">
            {t(keys.orders.paymentSectionTitle)}
          </span>
          <span className="flex items-center gap-1.5">
            <PayMethodGlyph method={order.paymentMethod} />
            <span className="text-[13px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
              {methodLabel}
            </span>
          </span>
        </div>
        <div className="rounded-[11px] px-3" style={{ background: insetBg }}>
          {payRows.map((r) => (
            <div key={r.label} className="flex items-center justify-between gap-3 py-[9px]">
              <span className="text-[12.5px] font-semibold text-[color:var(--text-secondary)]">
                {r.label}
              </span>
              <span
                className="text-[14px] font-extrabold tracking-[-0.01em]"
                style={{ color: r.pos ? "var(--success-500)" : "var(--warning-500)" }}
              >
                {r.amount}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* items */}
      <div className="mb-[14px] overflow-hidden rounded-[15px] bg-[color:var(--soft)]">
        {lines.map((it) => (
          <div
            key={`${it.productId}-${it.productVariantId}`}
            className="flex items-center gap-[11px] border-b border-[color:var(--hair)] px-[13px] py-[9px]"
          >
            <ImageThumb
              src={getOrderItemImageUrl(it)}
              seed={it.productId}
              alt={resolveItemTitle(it, locale)}
              size="h-[34px] w-[34px]"
              bg="bg-white"
              className="rounded-[9px]"
            />
            <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-[color:var(--text-primary)]">
              {resolveItemTitle(it, locale)}
            </span>
            <span className="shrink-0 text-[12.5px] font-semibold text-[color:var(--text-muted)]">
              ×{it.quantity}
            </span>
          </div>
        ))}
        {extra > 0 ? (
          <div className="border-b border-[color:var(--hair)] px-[13px] py-[9px] text-[12.5px] font-semibold text-[color:var(--text-muted)]">
            +{extra} {t(keys.orders.propItems).toLowerCase()}
          </div>
        ) : null}
        <div className="flex items-center justify-between gap-3 px-[15px] py-[13px]">
          <span className="text-[13px] font-semibold text-[color:var(--text-muted)]">
            {t(keys.orders.orderTotalLabel)}
          </span>
          <span className="text-[16px] font-extrabold tracking-[-0.01em] text-[color:var(--text-primary)]">
            {money.headlineFormatted}
          </span>
        </div>
      </div>

      {/* actions — primary CTA uses the design's black accent (matches the
          full-order screen), not a tone tint */}
      {meta.transition && av ? (
        <button
          type="button"
          onClick={runAction}
          className="mb-[9px] flex w-full items-center justify-center gap-2 rounded-[14px] bg-[color:var(--accent-500)] px-4 py-[15px] text-[15px] font-bold text-[color:var(--on-accent)] transition-opacity hover:opacity-90"
        >
          <ActionGlyph kind={av.icon} />
          {t(meta.nextLabelKey)}
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => onOpenFull(order.id)}
        className="w-full rounded-[14px] px-4 py-[13px] text-[14px] font-semibold text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
      >
        {t(keys.orders.viewFullOrder)}
      </button>
    </div>
  );
}

const ICON = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 1.9,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};
function XIcon() {
  return (
    <svg {...ICON} width={16} height={16} strokeWidth={2}>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}
function PhoneIcon() {
  return (
    <svg {...ICON} width={16} height={16}>
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.7a2 2 0 0 1-.5 2.1L8 9.6a16 16 0 0 0 6 6l1.1-1.1a2 2 0 0 1 2.1-.5c.9.3 1.8.5 2.7.6a2 2 0 0 1 1.7 2Z" />
    </svg>
  );
}
function WhatsAppIcon() {
  return (
    <svg {...ICON} width={16} height={16}>
      <path d="M21 11.5a8.5 8.5 0 0 1-12.3 7.6L3 21l1.9-5.7A8.5 8.5 0 1 1 21 11.5Z" />
    </svg>
  );
}
